import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { format } from 'date-fns';
import { Store } from '@ngrx/store';
import { System, EvChRT, EvChRTV2, AHEvChRT, isNotNullOrUndefined, PlantEmsEnumType, ProductModelService, AHevchStrategyEnum } from 'ngx-core-services';
import { map, startWith, switchMap, tap, catchError } from 'rxjs/operators';
import { EChartsOption } from 'echarts';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  SystemsState,
  selectedSystemUid,
  selectSystemEvchargeTelemetryBySelectedSystem,
  selectedEvchargeTelemetryRequestStatus,
  requestSystemEvchargeDailyTelemetry,
} from '../../store';
import {
  DayTelemetryData,
  EvChargeSession,
  BatteryInfo,
  ConnextMetrics,
  AzzurroHubMetrics,
  BatteryUtilizationSummary
} from './evcharge-telemetry.model';
import { EvchargeSessionChartService } from '../services/evcharge-session-chart.service';

const getLabels = () => ({
  title: 'Daily Charging Sessions',
  days: 'Days',
  dailyTotalEnergyDelivered: 'Daily total energy delivered',
  dailyTotalSessions: 'Daily Total Sessions',
  noDataForTheSelectedPeriod: 'No data for the selected period',
  selectSessionToViewDetails: 'Select a session to view details',
  session: 'Session',
  duration: 'Duration',
  energy: 'Energy',
  maxPower: 'Max Power',
  inProgress: 'In Progress',
  strategy: 'Strategy',
  minutes: 'minutes',
  hours: 'hours',
  parallelModeDescription: 'When enabled, multiple chargers can operate in parallel, sharing the available power.',
  batteryProtectionDescription: 'When enabled, the system prevents battery discharge beyond the selected limit value.',
  powerImportLimitDescription: 'When enabled, the configured percentage limits the imported power in relation to the contractual power.',
  moreDetails: 'More Details',
  batteryProtection: 'Battery Protection',
  socLimit: 'SOC Limit',
  powerImportLimit: 'Power Import Limit',
  powerImportLimitValue: 'Limit Value',
  parallelMode: 'Parallel Mode',
  enable: 'Enable',
  disable: 'Disable',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'lib-evcharge-telemetry',
  templateUrl: './evcharge-telemetry.component.html',
  styleUrls: ['./evcharge-telemetry.component.scss'],
})
export class EvchargeTelemetryComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  AHevchStrategyEnum = AHevchStrategyEnum;
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));

  readonly requestStatus$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      this.store.select(selectedEvchargeTelemetryRequestStatus(instanceId))
    ),
    isNotNullOrUndefined(),
  );

  readonly requestData$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([deviceId, systemId, date, forceRefresh, instanceId]) => {
      this.store.dispatch(requestSystemEvchargeDailyTelemetry({
        systemId,
        instanceId,
        range: { from: date, to: date },
        deviceId,
        forceRefresh
      }));
    }),
  );

  readonly requestIsPending$ = this.requestStatus$.pipe(
    map((status) => status === 'pending'),
    startWith(false),
    isNotNullOrUndefined(),
  );

  readonly evchargeTelemetryData$ = combineLatest([
    this._instanceId$.pipe(
      switchMap((instanceId) =>
        this.store.select(selectSystemEvchargeTelemetryBySelectedSystem(instanceId))
      )
    ),
    this.system$
  ]).pipe(
    map(([rawData, selectedSystem]) => {
      if (!selectedSystem) {
        return this.adaptDayData(rawData);
      }

      return this.adaptDayData(rawData);
    }),
    tap((data) => {
      this.selectedSession$.next(data.sessions[0]);
    }),
    catchError(() => of(this.getEmptyTelemetryData()))
  );

  options: ('power'|'energy')[] = ['power','energy'];
  selectedSources$: BehaviorSubject<('power'|'energy')[]> = new BehaviorSubject<('power'|'energy')[]>(['power','energy']);

  selectedSession$: BehaviorSubject<EvChargeSession | null> = new BehaviorSubject<EvChargeSession | null>(null);

  zoomed = false;
  private readonly zoomed$ = new BehaviorSubject<boolean>(false);

  readonly chartOptions$: Observable<EChartsOption | null> = combineLatest([
    this.selectedSession$,
    this.selectedSources$,
    this.zoomed$,
  ]).pipe(
    map(([session, sources, zoomed]) => {
      if (!session) {
        return null
      }
      const chart = this.evchargeSessionChartService.buildCombinedSessionChart(session, sources);
      if (!zoomed) {
        return { ...chart, dataZoom: [], grid: { ...(chart.grid || {}), bottom: '10' } };
      }
      return chart;
    }),
  );

  toggleZoom() {
    this.zoomed = !this.zoomed;
    this.zoomed$.next(this.zoomed);
  }

  showBatteryDetails = false;
  now: Date = new Date();

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private readonly evchargeSessionChartService: EvchargeSessionChartService,
  ) { }

  onDateSelected(date: string) {
    this.dateSelection$.next(date);
    this.selectedSession$.next(null); // Reset selection when date changes
  }

  selectSession(session: EvChargeSession) {
    this.showBatteryDetails = false;
    this.selectedSession$.next(session);
  }

  clickSource(selectedSources: ('power'|'energy')[]) {
    this.selectedSources$.next(selectedSources);
  }

  trackBySessionTid(_index: number, session: EvChargeSession): string {
    return session.tid;
  }

  private groupSessionsByTID(data: EvChRT[] | EvChRTV2[] | AHEvChRT[]): Record<string, any[]> {
    return (data as any).reduce((acc: Record<string, any[]>, item: any) => {
      if (!acc[item.TID]) {
        acc[item.TID] = [];
      }
      acc[item.TID].push(item);
      return acc;
    }, {});
  }

  private createSessionFromDataV2(
    tid: string,
    data: any[],
    dataloggerType: 'connext' | 'azzurrohub'
  ): EvChargeSession {
    // Ordina per timestamp
    const sortedData = data.sort((a: any, b: any) => a.TS - b.TS);

    // Normalizza i campi numerici a livello di dataset:
    // - se la telemetria è di tipo EvChRTV2 (Connext V2), converti SEM Wh->kWh
    const normalizedData = sortedData.map((d: any) => {
      if (d.__typename === 'EvChRTV2') {
        return {
          ...d,
          SEM: (d.SEM || 0) / 1000,
        };
      }
      return d;
    });

    const first = normalizedData[0];
    const last = normalizedData[normalizedData.length - 1];

    // Con SEM già normalizzato per EvChRTV2, qui non servono conversioni
    const totalEnergy = parseFloat(((last.SEM || 0) % 1 === 0 ? (last.SEM || 0) : (last.SEM || 0).toFixed(2)).toString());

    const maxPower = Math.max(...normalizedData.map((d: any) => d.CP / 1000));

    const duration = last.CT; // Duration in minutes

    // Cross-day detection
    const sessionSpanHours = (last.TS - first.TS) / 3600;
    const firstDay = new Date(first.TS * 1000).getDate();
    const lastDay = new Date(last.TS * 1000).getDate();
    const isCrossDay = sessionSpanHours > 20 || firstDay !== lastDay;

    // Datalogger-specific information
    let batteryInfo: BatteryInfo | undefined;
    let additionalMetrics: ConnextMetrics | AzzurroHubMetrics | undefined;

    if (dataloggerType === 'azzurrohub') {
      const azzurroData = data as AHEvChRT[];
      batteryInfo = this.extractBatteryInfo(azzurroData);
      additionalMetrics = this.extractAzzurroHubMetrics(azzurroData);
    } else if (dataloggerType === 'connext') {
      // Check if it's EvChRTV2 (has additional V2 fields) or legacy EvChRT
      const connextData = data as EvChRTV2[] | EvChRT[];
      additionalMetrics = this.extractConnextMetrics(connextData);
    }

    return {
      tid,
      startTime: new Date(first.TS * 1000),
      endTime: last.ST === 3 ? undefined : new Date(last.TS * 1000), // 3 = Charging
      duration,
      totalEnergy,
      maxPower,
      rawData: normalizedData,
      isCrossDay,
      dataPoints: normalizedData.length,

      // V2 specific information
      dataloggerType,
      batteryInfo,
      additionalMetrics,
    };
  }

  /**
   * Determine datalogger type from data
   */
  private getDataloggerTypeFromData(data: EvChRT[] | EvChRTV2[] | AHEvChRT[]): 'connext' | 'azzurrohub' {
    if (!data.length) return 'connext';
    const firstItem = data[0] as any;
    return firstItem.__typename === 'AHEvChRT' ? 'azzurrohub' : 'connext';
  }

  /**
   * Extract battery information for AzzurroHub
   */
  private extractBatteryInfo(data: AHEvChRT[]): BatteryInfo {
    const latest = data[data.length - 1];
    return {
      strategy: latest.evchStrategy,
      batteryProtectionEnabled: latest.evchBatteryProtectEna,
      socLimit: latest.evchBatterySOCLimit,
      powerImportLimitEnabled: latest.evchPwrImportLimit,
      powerImportLimitValue: latest.evchPwrImportValue,
      parallelEnabled: latest.evchParallelEna,
    };
  }

  /**
   * Extract AzzurroHub metrics
   */
  private extractAzzurroHubMetrics(data: AHEvChRT[]): AzzurroHubMetrics {
    const latest = data[data.length - 1];

    // Calculate aggregate statistics
    const avgBatterySOCLimit = (data as any).reduce((sum: number, d: any) => sum + d.evchBatterySOCLimit, 0) / data.length;
    const powerImportActiveCount = data.filter(d => d.evchPwrImportLimit).length;
    const batteryProtectionActiveCount = data.filter(d => d.evchBatteryProtectEna).length;

    return {
      deviceType: latest.deviceType,
      timezone: latest.TZ,
      avgBatterySOCLimit,
      powerImportUtilization: (powerImportActiveCount / data.length) * 100,
      batteryProtectionActiveTime: (batteryProtectionActiveCount / data.length) * 100,
    };
  }

  /**
   * Extract Connext metrics
   */
  private extractConnextMetrics(data: EvChRTV2[] | EvChRT[]): ConnextMetrics {
    // Check if it's V2 data with additional fields
    const firstItem = data[0] as any;
    const hasV2Fields = 'SOC' in firstItem && 'pcv' in firstItem && 'AE' in firstItem;

    if (hasV2Fields) {
      const v2Data = data as EvChRTV2[];
      return {
        averageSOC: (v2Data as any).reduce((sum: number, d: any) => sum + (d.SOC || 0), 0) / v2Data.length,
        averagePCV: (v2Data as any).reduce((sum: number, d: any) => sum + (d.pcv || 0), 0) / v2Data.length,
        activeEnergy: v2Data[v2Data.length - 1].AE || 0,
      };
    }

    // Legacy EvChRT data - no additional metrics available
    return {};
  }

  /**
   * Calculate battery utilization summary for AzzurroHub
   */
  private calculateBatteryUtilization(sessions: EvChargeSession[]): BatteryUtilizationSummary {
    const azzurroSessions = sessions.filter(s => s.dataloggerType === 'azzurrohub');

    if (!azzurroSessions.length) {
      return {
        averageSOCLimit: 0,
        protectionActivePercentage: 0,
        powerLimitActivePercentage: 0,
        parallelActivePercentage: 0,
      };
    }

    const avgSOCLimit = (azzurroSessions as any).reduce((sum: number, s: any) => {
      const metrics = s.additionalMetrics as AzzurroHubMetrics;
      return sum + (metrics?.avgBatterySOCLimit || 0);
    }, 0) / azzurroSessions.length;

    const avgProtectionActive = (azzurroSessions as any).reduce((sum: number, s: any) => {
      const metrics = s.additionalMetrics as AzzurroHubMetrics;
      return sum + (metrics?.batteryProtectionActiveTime || 0);
    }, 0) / azzurroSessions.length;

    const avgPowerLimitActive = (azzurroSessions as any).reduce((sum: number, s: any) => {
      const metrics = s.additionalMetrics as AzzurroHubMetrics;
      return sum + (metrics?.powerImportUtilization || 0);
    }, 0) / azzurroSessions.length;

    const parallelActiveSessions = azzurroSessions.filter(s => s.batteryInfo?.parallelEnabled).length;
    const parallelActivePercentage = (parallelActiveSessions / azzurroSessions.length) * 100;

    return {
      averageSOCLimit: avgSOCLimit,
      protectionActivePercentage: avgProtectionActive,
      powerLimitActivePercentage: avgPowerLimitActive,
      parallelActivePercentage,
    };
  }

  adaptDayData(rawData: EvChRT[] | EvChRTV2[] | AHEvChRT[]): DayTelemetryData {
    if (!rawData?.length) {
      return this.getEmptyTelemetryData();
    }

    const dataloggerType = this.getDataloggerTypeFromData(rawData);

    const sessionsByTID = this.groupSessionsByTID(rawData);
    const sessions = Object.entries(sessionsByTID).map(([tid, data]) =>
      this.createSessionFromDataV2(tid, data, dataloggerType)
    );

    const batteryUtilization = dataloggerType === 'azzurrohub'
      ? this.calculateBatteryUtilization(sessions)
      : undefined;

    return {
      date: this.dateSelection$.value,
      totalEnergy: (sessions as any).reduce((sum: number, s: any) => sum + s.totalEnergy, 0),
      totalSessions: sessions.length,
      sessions,
      hasData: sessions.length > 0,
      dataloggerType,
      batteryUtilization,
    };
  }

  private getEmptyTelemetryData(): DayTelemetryData {
    return {
      date: this.dateSelection$.value,
      totalEnergy: 0,
      totalSessions: 0,
      sessions: [],
      hasData: false,
    };
  }
}
